import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer';
import { updateDocumentCounts } from '@/pages/dashboard/dashboard.slice';

/**
 * Custom hook for managing document counts efficiently
 * Provides optimized methods for updating document counts across the application
 */
export const useDocumentCountsManager = () => {
  const dispatch = useDispatch();
  const currentSiteId = useSelector(state => state.dashboardPage.currentSiteId);
  const currentCounts = useSelector(state => state.dashboardPage.documentCounts);

  /**
   * Updates all document counts by refetching from server
   * Use this for comprehensive count updates
   */
  const refreshAllCounts = useCallback(async (siteId = currentSiteId) => {
    try {
      const [favouritesResult, allDocumentsResult] = await Promise.all([
        dispatch(dashboardApi.endpoints.getFavouriteDocumentsCount.initiate(siteId)).unwrap(),
        dispatch(dashboardApi.endpoints.getDocumentsCount.initiate(siteId)).unwrap()
      ]);

      dispatch(updateDocumentCounts({
        favourites: favouritesResult,
        allItems: allDocumentsResult,
        loading: false
      }));
      
      return {
        favourites: favouritesResult,
        allItems: allDocumentsResult
      };
    } catch (error) {
      console.error('Failed to refresh document counts:', error);
      throw error;
    }
  }, [dispatch, currentSiteId]);

  /**
   * Updates specific document counts
   * Use this for targeted count updates
   */
  const updateSpecificCounts = useCallback((countsUpdate) => {
    dispatch(updateDocumentCounts({
      ...currentCounts,
      ...countsUpdate,
      loading: false
    }));
  }, [dispatch, currentCounts]);

  /**
   * Optimistically adjusts favourite count
   * Use this for immediate UI feedback
   */
  const adjustFavouriteCount = useCallback((delta) => {
    const newCount = Math.max(0, currentCounts.favourites + delta);
    dispatch(updateDocumentCounts({
      ...currentCounts,
      favourites: newCount
    }));
  }, [dispatch, currentCounts]);

  /**
   * Creates a count update function with specific parameters
   * Use this to create a reusable update function for components
   */
  const createCountUpdater = useCallback((siteId = currentSiteId) => {
    return async () => {
      return await refreshAllCounts(siteId);
    };
  }, [refreshAllCounts, currentSiteId]);

  return {
    refreshAllCounts,
    updateSpecificCounts,
    adjustFavouriteCount,
    createCountUpdater,
    currentCounts
  };
};

export default useDocumentCountsManager;
